import { useMemo } from 'react';

import {
  FieldWrapper,
  InputErrorMessage,
  Label,
  MultiSelect,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@commenda/kakashi';
import {
  AgentFormResponseMode,
  TSysAgentStepUploadSource,
  TWorkflowDefinition,
} from '@commenda/types';

import { WorkflowGraphHelper, WorkflowStepPickerHelper } from '../../helpers';

const UPLOAD_MODE_LABELS: Readonly<Record<AgentFormResponseMode, string>> = {
  [AgentFormResponseMode.NONE]: 'None',
  [AgentFormResponseMode.ALL_PREVIOUS]: 'All previous upload steps',
  [AgentFormResponseMode.SELECTED]: 'Selected steps',
};

interface IAgentUploadSourceFieldProps {
  workflow: TWorkflowDefinition | null;
  agentStepId: string;
  source: TSysAgentStepUploadSource | undefined;
  onChange: (source: TSysAgentStepUploadSource) => void;
}

export const AgentUploadSourceField = ({
  workflow,
  agentStepId,
  source,
  onChange,
}: IAgentUploadSourceFieldProps) => {
  // An omitted source is read as all previous uploads, so the field shows it that way.
  const mode = source?.mode ?? AgentFormResponseMode.ALL_PREVIOUS;

  // Memoised because MultiSelect re-seeds its ticks whenever defaultValue changes identity.
  const selectedStepIds = useMemo(
    () =>
      source?.mode === AgentFormResponseMode.SELECTED
        ? (source.stepIds ?? [])
        : [],
    [source]
  );

  const uploadStepOptions = useMemo(
    () =>
      WorkflowStepPickerHelper.buildStepPickerOptions(
        workflow,
        WorkflowGraphHelper.findUpstreamFormSteps(workflow, agentStepId),
        selectedStepIds
      ),
    [workflow, agentStepId, selectedStepIds]
  );

  const strandedCount = uploadStepOptions.filter(
    (option) => option.isStranded
  ).length;

  const onModeChange = (value: string) => {
    const nextMode = Object.values(AgentFormResponseMode).find(
      (candidate) => candidate === value
    );
    if (!nextMode) return;
    onChange(
      nextMode === AgentFormResponseMode.SELECTED
        ? { mode: nextMode, stepIds: selectedStepIds }
        : { mode: nextMode }
    );
  };

  return (
    <FieldWrapper>
      <Label>Feed in uploads</Label>
      <Select value={mode} onValueChange={onModeChange}>
        <SelectTrigger>
          <SelectValue placeholder="Select which uploads to feed in" />
        </SelectTrigger>
        <SelectContent>
          {Object.values(AgentFormResponseMode).map((uploadMode) => (
            <SelectItem key={uploadMode} value={uploadMode}>
              {UPLOAD_MODE_LABELS[uploadMode]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {mode === AgentFormResponseMode.SELECTED && (
        <>
          <MultiSelect
            key={agentStepId}
            options={uploadStepOptions}
            defaultValue={selectedStepIds}
            onValueChange={(stepIds) =>
              onChange({ mode: AgentFormResponseMode.SELECTED, stepIds })
            }
            placeholder="Select upload steps"
            notFoundText="No upload step runs before this one. Connect one first."
          />
          {strandedCount > 0 && (
            <InputErrorMessage
              message={
                strandedCount === 1
                  ? 'One selected upload step no longer runs before this step. Untick it, or reconnect it earlier.'
                  : `${strandedCount} selected upload steps no longer run before this step. Untick them, or reconnect them earlier.`
              }
            />
          )}
        </>
      )}
    </FieldWrapper>
  );
};
